import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scrolls to the top (or to the anchor) on every route change.
 *
 * React Router 6 with the declarative `<Routes>` API does not restore scroll
 * position across navigations, so a `<Link>` click from the bottom of one page
 * leaves the next page scrolled to the same offset. This component fixes that.
 *
 * If the new location has a hash, scroll to the matching element instead — the
 * browser handles this for plain `<a>` tags but not for `<Link>`-based
 * navigations.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView();
        return;
      }
    }
    // MainLayout uses a fixed app shell with <main id="main-content"> as
    // the inner scroll container (B-039). Scroll that instead of the
    // window. Other routes (landing/auth/mobile shells) also carry
    // id="main-content" as the skip-link target (#882), but their <main>
    // is a plain flow element — the page itself scrolls, not <main> — so
    // `id` alone can't distinguish the two. `scrollHeight > clientHeight`
    // can: it's only true when <main> is actually the overflow container.
    const main = document.getElementById('main-content');
    if (main && main.scrollHeight > main.clientHeight) main.scrollTo(0, 0);
    else window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
}
